import {Controller} from "stimulus"

export default class extends Controller {
    connect() {
        this.resizeDialog = null
        this.currentAttachment = null
        this.currentFigure = null
        this.dialogId = Math.random().toString(36).substring(2, 10)
        this.boundHandleClick = this.handleClick.bind(this)
        this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)

        var editorElement = this.element.querySelector("trix-editor")
        if (editorElement) {
            editorElement.addEventListener("click", this.boundHandleClick)
        }
    }

    disconnect() {
        var editorElement = this.element.querySelector("trix-editor")
        if (editorElement) {
            editorElement.removeEventListener("click", this.boundHandleClick)
        }
        this.removeDialog()
    }

    handleClick(event) {
        var figure = event.target.closest("figure.attachment")
        if (!figure) {
            this.removeDialog()
            return
        }

        var img = figure.querySelector("img")
        if (!img) {
            this.removeDialog()
            return
        }

        var attachment = this.findAttachmentForFigure(figure)
        if (!attachment) {
            this.removeDialog()
            return
        }

        event.preventDefault()
        this.showResizeDialog(figure, img, attachment)
    }

    handleOutsideClick(event) {
        if (this.resizeDialog && !this.resizeDialog.contains(event.target)) {
            var figure = event.target.closest("figure.attachment")
            if (!figure || !this.element.contains(figure)) {
                this.removeDialog()
            }
        }
    }

    findAttachmentForFigure(figure) {
        var editorElement = this.element.querySelector("trix-editor")
        if (!editorElement || !editorElement.editor) return null

        var attachments = editorElement.editor.getDocument().getAttachments()
        var img = figure.querySelector("img")
        if (!img) return null

        var imgSrc = img.src

        // Try matching by URL first
        var found = attachments.find(function(a) {
            var url = a.getURL()
            if (!url) return false
            return imgSrc.includes(url) || url.includes(imgSrc)
        })

        // Fallback: match by data-trix-attachment JSON on the figure
        if (!found) {
            var trixDataStr = figure.getAttribute("data-trix-attachment")
            if (trixDataStr) {
                try {
                    var trixData = JSON.parse(trixDataStr)
                    if (trixData.url) {
                        found = attachments.find(function(a) {
                            var url = a.getURL()
                            return url && (trixData.url.includes(url) || url.includes(trixData.url))
                        })
                    }
                } catch (e) {
                    // JSON parse failed, skip fallback
                }
            }
        }

        // Last resort: if there's only one image attachment, use it
        if (!found) {
            var imageAttachments = attachments.filter(function(a) {
                var attrs = a.getAttributes()
                var ct = attrs.contentType || attrs["content-type"] || ""
                return ct.startsWith("image/")
            })
            if (imageAttachments.length === 1) {
                found = imageAttachments[0]
            }
        }

        return found || null
    }

    showResizeDialog(figure, img, attachment) {
        this.removeDialog()
        this.currentAttachment = attachment
        this.currentFigure = figure

        var maxDimension = this.maxDimension()
        var attrs = this.currentAttachment.getAttributes()
        var naturalWidth = img.naturalWidth || 0
        var naturalHeight = img.naturalHeight || 0

        // Prefer persisted resize dimensions from blob metadata (rendered as
        // data-resize-width / data-resize-height on the <img> by _blob.html.erb).
        // These survive page reloads, whereas Trix attachment attributes may
        // revert to the original image dimensions.
        var savedResizeWidth = parseInt(img.dataset.resizeWidth, 10)
        var savedResizeHeight = parseInt(img.dataset.resizeHeight, 10)

        var originalWidth = (Number.isFinite(savedResizeWidth) && savedResizeWidth > 0)
            ? savedResizeWidth
            : parseInt(attrs.width, 10)
        if (!Number.isFinite(originalWidth) || originalWidth <= 0) {
            originalWidth = naturalWidth
        }
        var originalHeight = (Number.isFinite(savedResizeHeight) && savedResizeHeight > 0)
            ? savedResizeHeight
            : parseInt(attrs.height, 10)
        if (!Number.isFinite(originalHeight) || originalHeight <= 0) {
            originalHeight = naturalHeight
        }

        // When the image hasn't loaded yet, naturalWidth/naturalHeight are 0
        // which would pre-fill the min=1 inputs with an invalid value.
        // Clamp to valid range so the dialog opens in a usable state.
        if (!Number.isFinite(originalWidth) || originalWidth <= 0) {
            originalWidth = ""
        } else if (originalWidth > maxDimension) {
            originalWidth = maxDimension
        }
        if (!Number.isFinite(originalHeight) || originalHeight <= 0) {
            originalHeight = ""
        } else if (originalHeight > maxDimension) {
            originalHeight = maxDimension
        }

        var aspectRatio
        if (originalWidth > 0 && originalHeight > 0) {
            aspectRatio = originalWidth / originalHeight
        } else if (naturalWidth > 0 && naturalHeight > 0) {
            aspectRatio = naturalWidth / naturalHeight
        } else {
            aspectRatio = 1
        }
        if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
            aspectRatio = 1
        }

        var dialog = document.createElement("div")
        dialog.className = "trix-image-resize-dialog"
        dialog.setAttribute("role", "dialog")
        dialog.setAttribute("aria-label", "Image Size")
        dialog.style.cssText = "position:absolute;z-index:1000;background:white;border:1px solid #d1d5db;border-radius:8px;padding:12px;box-shadow:0 4px 12px rgba(0,0,0,0.15);min-width:240px;"

        var title = document.createElement("div")
        title.style.cssText = "font-weight:600;font-size:13px;margin-bottom:8px;color:#374151;"
        title.textContent = "Image Size"
        dialog.appendChild(title)

        var fieldsRow = document.createElement("div")
        fieldsRow.style.cssText = "display:flex;gap:8px;align-items:center;margin-bottom:8px;"

        var widthId = "trix-resize-width-" + this.dialogId
        var heightId = "trix-resize-height-" + this.dialogId
        var widthInput = this.buildDimensionInput(widthId, "Width", originalWidth, maxDimension)
        var heightInput = this.buildDimensionInput(heightId, "Height", originalHeight, maxDimension)
        fieldsRow.appendChild(widthInput.wrapper)
        fieldsRow.appendChild(heightInput.wrapper)
        dialog.appendChild(fieldsRow)

        var lockRow = document.createElement("div")
        lockRow.style.cssText = "display:flex;align-items:center;margin-bottom:8px;"
        var lockLabel = document.createElement("label")
        lockLabel.style.cssText = "font-size:11px;color:#6b7280;display:flex;align-items:center;gap:4px;cursor:pointer;"
        var lockId = "trix-resize-lock-ratio-" + this.dialogId
        lockLabel.setAttribute("for", lockId)
        var lockRatioCheckbox = document.createElement("input")
        lockRatioCheckbox.type = "checkbox"
        lockRatioCheckbox.id = lockId
        lockRatioCheckbox.checked = true
        lockRatioCheckbox.style.margin = "0"
        lockLabel.appendChild(lockRatioCheckbox)
        lockLabel.appendChild(document.createTextNode("Lock aspect ratio"))
        lockRow.appendChild(lockLabel)
        dialog.appendChild(lockRow)

        // Status message area
        var statusRow = document.createElement("div")
        statusRow.style.cssText = "font-size:11px;margin-bottom:6px;min-height:16px;"
        dialog.appendChild(statusRow)

        var buttonRow = document.createElement("div")
        buttonRow.style.cssText = "display:flex;gap:6px;"
        var applyButton = document.createElement("button")
        applyButton.type = "button"
        applyButton.style.cssText = "flex:1;padding:4px 8px;background:#7c3aed;color:white;border:none;border-radius:4px;font-size:12px;cursor:pointer;"
        applyButton.textContent = "Apply"
        var resetButton = document.createElement("button")
        resetButton.type = "button"
        resetButton.style.cssText = "flex:1;padding:4px 8px;background:#f3f4f6;color:#374151;border:1px solid #d1d5db;border-radius:4px;font-size:12px;cursor:pointer;"
        resetButton.textContent = "Reset"
        buttonRow.appendChild(applyButton)
        buttonRow.appendChild(resetButton)
        dialog.appendChild(buttonRow)

        var figureRect = figure.getBoundingClientRect()
        var editorRect = this.element.getBoundingClientRect()
        dialog.style.top = (figureRect.bottom - editorRect.top + 4) + "px"
        dialog.style.left = (figureRect.left - editorRect.left) + "px"

        this.originalPosition = this.element.style.position
        this.element.style.position = "relative"
        this.element.appendChild(dialog)
        this.resizeDialog = dialog
        document.addEventListener("click", this.boundHandleOutsideClick)
        widthInput.input.focus()

        var self = this

        widthInput.input.addEventListener("input", function() {
            if (lockRatioCheckbox.checked && Number.isFinite(aspectRatio) && aspectRatio > 0) {
                var w = parseInt(widthInput.input.value, 10)
                var newH = Math.round(w / aspectRatio)
                if (Number.isFinite(w) && w > 0 && Number.isFinite(newH) && newH > 0) {
                    heightInput.input.value = newH
                }
            }
        })

        heightInput.input.addEventListener("input", function() {
            if (lockRatioCheckbox.checked && Number.isFinite(aspectRatio) && aspectRatio > 0) {
                var h = parseInt(heightInput.input.value, 10)
                var newW = Math.round(h * aspectRatio)
                if (Number.isFinite(h) && h > 0 && Number.isFinite(newW) && newW > 0) {
                    widthInput.input.value = newW
                }
            }
        })

        applyButton.addEventListener("click", function() {
            var width = Math.round(widthInput.input.valueAsNumber)
            var height = Math.round(heightInput.input.valueAsNumber)
            self.applyResize(
                width,
                height,
                statusRow, applyButton
            )
        })

        resetButton.addEventListener("click", function() {
            self.resetResize(statusRow, resetButton)
        })
    }

    maxDimension() {
        var value = parseInt(this.data.get("maxDimensionValue"), 10)
        return Number.isFinite(value) && value > 0 ? value : 4000
    }

    // The form's save button, named in the success messages. Set it with
    // data-trix-image-resize-save-label; CMS page blocks rely on the default.
    saveLabel() {
        return this.data.get("saveLabel") || "Update Page"
    }

    buildDimensionInput(id, labelText, value, maxDimension) {
        var wrapper = document.createElement("div")
        wrapper.style.flex = "1"
        var label = document.createElement("label")
        label.style.cssText = "font-size:11px;color:#6b7280;display:block;margin-bottom:2px;"
        label.setAttribute("for", id)
        label.textContent = labelText
        var input = document.createElement("input")
        input.type = "number"
        input.id = id
        input.value = value
        input.min = "1"
        input.max = String(maxDimension)
        input.style.cssText = "width:100%;padding:4px 6px;border:1px solid #d1d5db;border-radius:4px;font-size:13px;"
        wrapper.appendChild(label)
        wrapper.appendChild(input)
        return {wrapper: wrapper, input: input}
    }

    buildRequestBody(attachment) {
        var attrs = attachment.getAttributes()
        var body = {}

        if (attrs.sgid) {
            body.sgid = attrs.sgid
        } else {
            // Try to extract signed_id from the URL
            var url = attrs.url || ""
            var match = url.match(/\/blobs\/(?:redirect\/)?([^\/]+)\//)
            if (match) {
                body.signed_id = decodeURIComponent(match[1])
            }
        }

        // Also try data-trix-attachment on the figure as fallback
        if (!body.sgid && !body.signed_id && this.currentFigure) {
            var trixDataStr = this.currentFigure.getAttribute("data-trix-attachment")
            if (trixDataStr) {
                try {
                    var trixData = JSON.parse(trixDataStr)
                    if (trixData.sgid) {
                        body.sgid = trixData.sgid
                    }
                } catch (e) {
                    // JSON parse failed
                }
            }
        }

        return body
    }

    showStatus(statusRow, message, color) {
        statusRow.textContent = message
        statusRow.style.color = color
    }

    patchBlobResize(body) {
        var csrfToken = document.querySelector('meta[name="csrf-token"]')
        if (!csrfToken) {
            return Promise.reject(new Error("Missing CSRF token"))
        }

        return fetch("/cms/blob_resize", {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": csrfToken.getAttribute("content"),
                "Accept": "application/json"
            },
            credentials: "same-origin",
            body: JSON.stringify(body)
        }).then(function(response) {
            if (!response.ok) {
                throw new Error("Server returned " + response.status)
            }
            var contentType = response.headers.get("Content-Type") || ""
            if (!contentType.includes("application/json")) {
                throw new Error("Unexpected response format")
            }
            return response.json()
        })
    }

    applyResize(width, height, statusRow, button) {
        var attachment = this.currentAttachment
        if (!attachment) return

        var maxDimension = this.maxDimension()
        var hasWidth = isFinite(width) && width >= 1
        var hasHeight = isFinite(height) && height >= 1

        if (!hasWidth && !hasHeight) {
            this.showStatus(statusRow, "Enter at least one dimension", "#dc2626")
            return
        }
        if (hasWidth && width > maxDimension) {
            this.showStatus(statusRow, "Width must be between 1 and " + maxDimension, "#dc2626")
            return
        }
        if (hasHeight && height > maxDimension) {
            this.showStatus(statusRow, "Height must be between 1 and " + maxDimension, "#dc2626")
            return
        }

        var body = this.buildRequestBody(attachment)
        if (!body.sgid && !body.signed_id) {
            this.showStatus(statusRow, "Cannot identify image blob", "#dc2626")
            return
        }

        body.width = hasWidth ? width : ""
        body.height = hasHeight ? height : ""

        var self = this
        button.disabled = true
        button.textContent = "Saving..."
        this.showStatus(statusRow, "Saving resize to Cloudinary...", "#6b7280")

        this.patchBlobResize(body)
            .then(function(data) {
                if (data.success) {
                    // Only update width/height on the Trix attachment for
                    // editor preview. Do NOT change the URL — the original
                    // ActiveStorage URL always works, while Cloudinary
                    // transform URLs may fail depending on account config.
                    // The _blob.html.erb template applies Cloudinary
                    // transforms at render time using the blob metadata
                    // saved by the server.
                    var newAttrs = {
                        width: hasWidth ? width : "",
                        height: hasHeight ? height : ""
                    }

                    // When only one dimension is provided, explicitly clear the
                    // other so Trix does not keep a stale attribute that can
                    // distort the image.
                    if (hasWidth && !hasHeight) newAttrs.height = ""
                    if (hasHeight && !hasWidth) newAttrs.width = ""

                    attachment.setAttributes(newAttrs)

                    // Keep data attributes on the <img> in sync so the dialog
                    // shows saved resize values when re-opened before page reload.
                    if (self.currentFigure) {
                        var figureImg = self.currentFigure.querySelector("img")
                        if (figureImg) {
                            if (hasWidth) { figureImg.dataset.resizeWidth = width } else { delete figureImg.dataset.resizeWidth }
                            if (hasHeight) { figureImg.dataset.resizeHeight = height } else { delete figureImg.dataset.resizeHeight }
                        }
                    }

                    self.showStatus(statusRow, "Resize applied. Click " + self.saveLabel() + " to publish.", "#059669")
                    button.disabled = false
                    button.textContent = "Apply"

                    setTimeout(function() { self.removeDialog() }, 1500)
                } else {
                    self.showStatus(statusRow, data.error || "Failed to resize", "#dc2626")
                    button.disabled = false
                    button.textContent = "Apply"
                }
            })
            .catch(function(error) {
                self.showStatus(statusRow, "Error: " + error.message, "#dc2626")
                button.disabled = false
                button.textContent = "Apply"
            })
    }

    resetResize(statusRow, button) {
        var attachment = this.currentAttachment
        if (!attachment) return

        var body = this.buildRequestBody(attachment)
        if (!body.sgid && !body.signed_id) {
            this.showStatus(statusRow, "Cannot identify image blob", "#dc2626")
            return
        }

        body.width = ""
        body.height = ""

        var self = this
        button.disabled = true
        button.textContent = "Resetting..."
        this.showStatus(statusRow, "Resetting to original size...", "#6b7280")

        this.patchBlobResize(body)
            .then(function(data) {
                if (data.success) {
                    // Reset attachment to original dimensions.
                    // Use a single setAttributes call to avoid stale DOM refs.
                    // Do NOT change the URL — keep the original ActiveStorage
                    // URL. Cloudinary transforms are applied at render time.
                    attachment.setAttributes({width: "", height: ""})

                    // Clear resize data attributes so the dialog falls back to
                    // original dimensions when re-opened.
                    if (self.currentFigure) {
                        var figureImg = self.currentFigure.querySelector("img")
                        if (figureImg) {
                            delete figureImg.dataset.resizeWidth
                            delete figureImg.dataset.resizeHeight
                        }
                    }

                    self.showStatus(statusRow, "Reset to original. Click " + self.saveLabel() + " to publish.", "#059669")
                    button.disabled = false
                    button.textContent = "Reset"

                    setTimeout(function() { self.removeDialog() }, 1500)
                } else {
                    self.showStatus(statusRow, "Failed to reset", "#dc2626")
                    button.disabled = false
                    button.textContent = "Reset"
                }
            })
            .catch(function(error) {
                self.showStatus(statusRow, "Error: " + error.message, "#dc2626")
                button.disabled = false
                button.textContent = "Reset"
            })
    }

    removeDialog() {
        if (this.resizeDialog) {
            document.removeEventListener("click", this.boundHandleOutsideClick)
            this.resizeDialog.remove()
            this.resizeDialog = null
            if (this.originalPosition !== undefined) {
                this.element.style.position = this.originalPosition
                this.originalPosition = undefined
            }
        }
        this.currentAttachment = null
        this.currentFigure = null
    }
}
